import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { Flight } from 'types';
import { differenceInCalendarDays } from 'date-fns';


const PERSISTENT_KEY = 'fp_avia_logs';
const VERSION = 2;
const HISTORY_SIZE = 10;

class State {
  records: Flight.HistoryRecord[] = [];
}

const getters = <GetterTree<State, any>>{
  records(state: State): Flight.HistoryRecord[] {
    return state.records.slice().sort((a, b) => b.updatedAt - a.updatedAt);
  },
}

const mutations = <MutationTree<State>>{

  setHistory(state: State, records: Flight.HistoryRecord[]) {
    state.records = records;
  },
}

const actions = <ActionTree<State, any>>{

  async init(store: ActionContext<State, any>) {
    const history = await readPersistentData();
    if (history && history.records.length > 0) {
      const filtered = history.records
        .filter(it => differenceInCalendarDays(it.criteria.departDate, new Date()) > 0);
      if (filtered.length > 0) {
        store.commit('setHistory', filtered);
        store.dispatch('syncPersistance');
      }
    }
  },

  async write(store: ActionContext<State, any>, record: Flight.HistoryRecord) {
    const history = await readPersistentData();
    const storagable: Flight.HistoryRecord[] = [];

    if (history) {
      const cloneIndex = history.records.findIndex(it => record.criteria.same(it.criteria));
      if (cloneIndex >= 0) {
        history.records.splice(cloneIndex, 1);
      }
      storagable.push(...history.records);
    }

    storagable.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, HISTORY_SIZE);
    storagable.push(record);

    localStorage.setItem(PERSISTENT_KEY, JSON.stringify({
      version: VERSION,
      records: storagable,
    }));
  },

  delete(store: ActionContext<State, any>, record: Flight.HistoryRecord) {
    const criteria = Flight.SearchCriteria.fromPersistance(record);
    const index = store.state.records.findIndex(it => criteria.same(it.criteria));
    if (index >= 0) {
      const cloned = store.state.records.slice();
      cloned.splice(index, 1);
      store.commit('setHistory', cloned);
      store.dispatch('syncPersistance');
    }
  },

  syncPersistance(store: ActionContext<State, any>) {
    localStorage.setItem(PERSISTENT_KEY, JSON.stringify({
      version: VERSION,
      records: store.state.records.slice().sort((a, b) => b.updatedAt - a.updatedAt)
        .slice(0, HISTORY_SIZE),
    }));
  }
}


const readPersistentData = async (): Promise<Flight.History> => {
  const json = localStorage.getItem(PERSISTENT_KEY);
  try {
    if (json == null) {
      throw new Error();
    }
    const parsed = await JSON.parse(json) as Flight.History;

    if (parsed.version !== VERSION) {
      throw new Error();
    }
    return parsed;
  } catch (e) {
    localStorage.removeItem(PERSISTENT_KEY);
    return null;
  }
}


export const flightsHistory = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions
}

export default {};
